import { Component, OnInit, Inject, OnDestroy, ViewChild } from "@angular/core";
import { FormControl, Validators } from "@angular/forms";
import { MAT_DIALOG_DATA, MatDialogRef, MatDialog } from "@angular/material/dialog";
import { MatPaginator } from "@angular/material/paginator";
import { ToastService } from "angular-toastify";
import { Subscription } from "rxjs";
import { VALID_AGES } from "src/app/shared/constants";
import { IAccount } from "src/app/shared/models/account.model";
import {
	ALG_TYPES,
	Adventure,
	AdventureLesson,
	CHEST_RARITY,
} from "src/app/shared/models/adventure.model";
import {
	IUploadImage,
	ImageFiles,
} from "src/app/shared/models/image-upload.model";
import { IActivity } from "src/app/shared/models/activities.model";
import { AdventureService } from "src/app/shared/services/adventure.service";
import { ImageUploaderService } from "src/app/shared/services/image-uploader.service";
import { LessonService } from "src/app/shared/services/lesson.service";
import { AccountStore } from "src/app/shared/services/stores/account.store.service";
import { Owner } from "src/environments/environment.model";
import { environment } from "src/environments/environment";
import { ActivityDescriptionService } from "src/app/shared/services/activity-description.service";
import { FirestoreService } from "src/app/shared/services/firestore.service";

interface ActivityWithLesson extends IActivity {
	lessonName: string;
	lessonId: string;
}

interface StarConfig {
	life: number;
	activities: any[];
	rewards: any[];
}

@Component({
	selector: "app-add-adventure",
	templateUrl: "./add-adventure.component.html",
	styleUrls: ["./add-adventure.component.scss"],
})
export class AddAdventureDialog implements OnInit, OnDestroy {
	@ViewChild('activitiesPaginator') activitiesPaginator!: MatPaginator;

	private addSubscription!: Subscription;
	private updateSubscription!: Subscription;
	private subscriptions!: Subscription;

	loading: boolean = true;
	selectingLessons: boolean = false;
	configuringCustomRecommendation: boolean = false;

	algTypes = ALG_TYPES;
	ageValues = VALID_AGES;
	chestRarity = CHEST_RARITY;

	owners: Owner[] = [];
	lessons!: AdventureLesson[];
	selectedLessons: AdventureLesson[] = [];

	nameControl = new FormControl(null, Validators.required);
	ownerControl = new FormControl(null, Validators.required);
	lessonsControl = new FormControl([], Validators.required);
	ageControl = new FormControl(null, Validators.required);
	recControl = new FormControl(null, Validators.required);

	isUploading = false;

	filteredOwners: Owner[] = [];
	filteredContent: any[] = [];

	constructor(
		public dialogRef: MatDialogRef<AddAdventureDialog>,
		@Inject(MAT_DIALOG_DATA) public data: Adventure,
		private accountStore: AccountStore,
		private lessonService: LessonService,
		private toastService: ToastService,
		private uploadService: ImageUploaderService,
		private adventureService: AdventureService,
		private dialog: MatDialog,
		private descriptionService: ActivityDescriptionService,
		private firestore: FirestoreService
	) {
		this.subscriptions = new Subscription();
	}

	ngOnInit() {
		this.loadData();

		this.nameControl.setValue(this.data.name);
		this.ownerControl.setValue(this.data.owner);
		this.lessonsControl.setValue(this.data.lessons);
		this.ageControl.setValue(this.data.age);
		this.recControl.setValue(this.data.recommendation.type);

		if (this.data.imageId != "") {
			this.imageExists = true;
			this.toVerify = true;
		}
	}

	ngOnDestroy(): void {
		if (this.subscriptions) {
			this.subscriptions.unsubscribe();
		}

		if (this.addSubscription) {
			this.addSubscription.unsubscribe();
		}

		if (this.updateSubscription) {
			this.updateSubscription.unsubscribe();
		}
	}

	private loadData() {
		this.loading = true;

		const accountSub = this.accountStore.listAccounts().subscribe((res) => {
			const accountOwners: Owner[] = (res as IAccount[])
				.sort((a, b) => a.displayName.localeCompare(b.displayName))
				.map(acc => ({ id: `accounts/${acc.uid}`, displayName: acc.displayName }));
			this.owners = [...environment.KNOWN_OWNERS, ...accountOwners];
			this.filteredOwners = [...this.owners];
		});

		const lessonSub = this.lessonService.getLessons().subscribe({
			next: (res) => {
				this.lessons = res
					.sort((a, b) => a.displayName.localeCompare(b.displayName))
					.map((lesson) => {
						return {
							lessonId: lesson.uid,
							age: lesson.age,
							displayName: lesson.displayName,
							version: lesson.version,
						} as AdventureLesson;
					});
				this.filterLessons();
			},
			error: (err) => {
				console.error(err);
				this.toastService.error("Failed to load lessons");
				this.lessons = [];
			},
			complete: () => {
				this.selectedLessons= this.data.lessons.slice().map(dataLesson => {
					const matchingLesson = this.lessons.find(lesson => lesson.lessonId === dataLesson.lessonId);
					return matchingLesson ? matchingLesson : dataLesson;
				});
				this.loading = false;
			},
		});

		this.subscriptions.add(accountSub);
		this.subscriptions.add(lessonSub);
	}

	//#region CUSTOM RECOMMENDATION METHODS

	startCustomConfig() {
		this.configuringCustomRecommendation = true;
	}

	onCustomConfigSaved(updatedAdventure: Adventure) {
		this.data = updatedAdventure;
		this.configuringCustomRecommendation = false;
	}

	onCustomConfigCancelled() {
		this.configuringCustomRecommendation = false;
	}

	//#endregion

	//#region LESSONS

	lessonSearchTerm: string = "";

	openLessonSelect() {}

	filterLessons() {
		this.filteredContent = this.lessons.filter(
			(lesson) =>
				!this.selectedLessons.some(
					(selected) => selected.lessonId === lesson.lessonId
				) &&
				(!this.lessonSearchTerm ||
					lesson.displayName
						.toLowerCase()
						.includes(this.lessonSearchTerm.toLowerCase()))
		);
	}

	addLesson(lesson: AdventureLesson) {
		this.selectedLessons.push(lesson);
		this.filterLessons();
	}

	removeLesson(lesson: AdventureLesson) {
		this.selectedLessons = this.selectedLessons.filter(
			(l) => l.lessonId !== lesson.lessonId
		);
		this.filterLessons();
	}

	//#endregion

	//#region SEARCH

	searchTerm: string = "";

	filterOptions<T extends { displayName: string }>(
		sourceList: T[],
		filteredList: T[],
		type: string
	) {
		if (!this.searchTerm) {
			filteredList.length = 0;
			filteredList.push(...sourceList);
			return;
		}

		const lowerSearch = this.searchTerm.toLowerCase();
		filteredList.length = 0;
		filteredList.push(
			...sourceList.filter((item) =>
				item.displayName.toLowerCase().includes(lowerSearch)
			)
		);
	}

	clearSearch<T>(sourceList: T[], filteredList: T[]) {
		this.searchTerm = "";
		filteredList.length = 0;
		filteredList.push(...sourceList);
	}

	onOwnerDropdownOpen() {
		this.filteredOwners = [...this.owners];
	}

	//#endregion

	//#region IMAGE UPLOAD

	public files: ImageFiles[] = [];
	imageExists: boolean = false;
	toVerify: boolean = false;
	imageVerify!: string;

	changeImage(newValue: string) {
		this.data.imageId = newValue;
		this.toVerify = false;
		this.files = [];
		this.imageExists = false;
	}

	verifyImage() {
		this.toVerify = true;
	}

	setFile(file: ImageFiles | null) {
		if (file === null) {
			this.data.imageId = "";
			this.files = [];
			return;
		}
		this.data.imageId = file.name;
		this.files.push(file);
	}

	//#endregion

	//#region Dialog Actions
	onCloseClick() {
		this.dialogRef.close();
	}

	onSaveClick() {
		let canUpload = true;
		this.isUploading = true;

		if (this.data.name === "") {
			this.nameControl.markAsTouched();
			canUpload = false;
		}

		if (this.data.owner === "") {
			this.ownerControl.markAsTouched();
			canUpload = false;
		}

		if (this.selectedLessons.length === 0) {
			this.toastService.error("No lesson has been selected.");
			this.lessonsControl.markAsTouched();
			canUpload = false;
		}

		if (this.data.age === "") {
			this.ageControl.markAsTouched();
			canUpload = false;
		}

		if (!this.data.recommendation) {
			this.recControl.markAsTouched();
			canUpload = false;
		}
		if (this.data.recommendation.type === "Classic") {
			this.data.recommendation = {
				type: "Classic",
			}
		}

		if (this.data.recommendation.type === 'Custom') {
			if (!this.data.recommendation.starConfig || this.data.recommendation.starConfig.length === 0) {
				this.toastService.error("Custom recommendation requires star configuration.");
				canUpload = false;
			}
		}

		if (!this.imageExists && this.files.length === 0) {
			this.toastService.error("No image has been selected.");
			this.isUploading = false;
			return;
		}

		if (!canUpload) {
			this.toastService.error("Failed to save new Adventure.");
			this.isUploading = false;
			return;
		}

		this.data.lessons = this.selectedLessons;

		if (this.files.length > 0) {
			const uploadFileIds = this.files.map((file) => {
				return {
					id: file.name,
					extension: "jpg",
				} as IUploadImage;
			});

			const fileBlobs = this.files.map((fileInfo) => {
				const imageBlob = new Blob([fileInfo.file], {
					type: `image/jpeg`,
				});
				return imageBlob;
			});

			this.uploadService
				.uploadImages(fileBlobs, uploadFileIds)
				.subscribe((res) => {
					if (!res) {
						this.toastService.error("Failed to upload image");
						this.isUploading = false;
						return;
					} else {
						this.toastService.success(
							"Image was uploaded successfully"
						);
						if (!this.data.uid || this.data.uid === "") {
							this.createAdventure();
						} else {
							this.updateAdventure();
						}
					}
				});
		} else {
			if (!this.data.uid || this.data.uid === "") {
				this.createAdventure();
			} else {
				this.updateAdventure();
			}
		}
	}

	createAdventure() {
		this.addSubscription = this.adventureService
			.createAdventure(this.data)
			.pipe()
			.subscribe({
				next: (res) => {
					if (!res) {
						this.toastService.error("Failed to create adventure.");
						return;
					}
					this.data.uid = res.uid;
					this.dialogRef.close(this.data);
				},
				error: (error) => {
					console.log(error);
					this.toastService.error("Failed to create adventure.");
					this.isUploading = false;
					return;
				},
			});
	}

	updateAdventure() {
		this.updateSubscription = this.adventureService
			.updateAdventure(this.data.uid, this.data)
			.pipe()
			.subscribe({
				next: () => {
					this.dialogRef.close(this.data);
				},
				error: (error) => {
					console.log(error);
					this.toastService.error("Failed to update adventure.");
					this.isUploading = false;
					return;
				},
			});
	}
	//#endregion
}
